import { CustomBox3D } from "./CustomBox3D";
import { Matrix4, Vector3 } from "@eplan/wgpurenderer/src/renderer.js";
import { createMaterial } from "../Utils/colorUtils";
import {
  loadEmaCadModel,
  prepareHighlight,
  getNodeBounds,
} from "./EmaCadLoader.js";
import api from "../../../../api/axios";
import { isDummyDuct } from "../../../../builders/dummyDucts";

export async function createCabinet3D({
  renderer,
  allocator,
  storeGeometry,
  materials,
  texture,
  snapshot,
  selectedProject,
  cabinetCadUrl,
  insideClearance,
}) {
  const { cabinet, ductTopEnclosure, ductLeft, ductRight } = snapshot;

  const width = cabinet?.size?.width ?? 0;
  const height = cabinet?.size?.height ?? 0;
  const depth = cabinet?.size?.depth ?? 600;
  const resolvedInsideClearance = insideClearance ?? cabinet?.insideClearance ?? 0;
  const cabinetName = "Enclosure";
  if (width <= 0 || height <= 0) return;

  // ✅ Root node (like <>...</>)
  const root = renderer.createInternalNode();
  root.name = selectedProject?.name;

  // ✅ Same grouping structure as the procedural version
  const cabinetInternalNode = renderer.createInternalNode();
  cabinetInternalNode.name = cabinetName;
  root.addChild(cabinetInternalNode);

  let cabinetNode = null;

  if (cabinetCadUrl) {
    try {
      const { node: loadedCabinetNode, referencePoint } = await loadEmaCadModel(
        renderer,
        allocator,
        cabinetCadUrl,
        true
      );
      await prepareHighlight(
        loadedCabinetNode,
        renderer,
        storeGeometry,
        texture,
        materials[6],
      );
      loadedCabinetNode.name = "Panels & Door"; // match procedural naming

      // Measure bounds against whatever matrix the loader already set —
      // DO NOT strip it first. Unlike device files, the cabinet's own
      // rotation (Z-up -> Y-up) needs to be preserved, so bounds here are
      // measured in the already-correctly-oriented space.
      const bounds = getNodeBounds(loadedCabinetNode, renderer);

      // Align the mesh's own corner to scene (0,0,0), same convention
      // CustomBox3D already uses for the procedural cabinet.
      const cornerTranslation = Matrix4.translation([
        -bounds.minX,
        -bounds.minY,
        -bounds.minZ, // ⚠️ may need maxZ instead
      ]);

      // compose on top of the existing matrix — preserves the rotation
      loadedCabinetNode.matrix = Matrix4.multiply(
        cornerTranslation,
        loadedCabinetNode.matrix,
      );
      // parent under cabinetInternalNode, not root — matches procedural tree
      cabinetInternalNode.addChild(loadedCabinetNode);
      cabinetNode = loadedCabinetNode;
    } catch (err) {
      console.error("Failed loading enclosure CAD:", err);

      cabinetNode = await CustomBox3D({
        renderer,
        allocator,
        storeGeometry,
        parent: cabinetInternalNode,
        material: materials[5],
        texture,
        x: 0,
        y: 0,
        z: 0,
        width,
        height,
        depth,
        nodeName: "Panels & Door",
      });
    }
  } else {
    cabinetNode = await CustomBox3D({
      renderer,
      allocator,
      storeGeometry,
      parent: cabinetInternalNode,
      material: materials[5],
      texture,
      x: 0,
      y: 0,
      z: 0,
      width,
      height,
      depth,
      nodeName: "Panels & Door",
    });
  }

  // ✅ Montage Plate — same nested grouping structure as procedural version
  const montage = snapshot?.montagePlate;
  const backPanelWidth = (depth - resolvedInsideClearance) / 2;
  const montageDepth = montage.mountingDistance + backPanelWidth;
  let montageNode = root;
  const montageInternalNode = renderer.createInternalNode();
  montageInternalNode.name = "Montage Plate";
  cabinetInternalNode.addChild(montageInternalNode);

  if (montage && !cabinetCadUrl) {
    montageNode = await CustomBox3D({
      renderer,
      allocator,
      storeGeometry,
      parent: montageInternalNode,
      material: materials[7],
      texture,
      x: montage.position.x,
      y: montage.position.y,
      z: montageDepth,
      width: montage.size.width,
      height: montage.size.height,
      depth: -montage.size.depth,
      nodeName: "Plate",
    });
  }

  // Placeholder ducts (no duct selected in the scenario) are marked by color
  // only and stay OPAQUE: the renderer does not sort transparent objects, so a
  // transparent duct behind the transparent cabinet CAD (panels/door) gets
  // discarded and becomes invisible.
  // `await` matters: if createMaterial is async, passing the un-awaited
  // Promise to storeGeometry makes the box invisible.
  const ductsDummy = isDummyDuct(ductTopEnclosure) || isDummyDuct(ductLeft);
  const ductsColor = ductsDummy ? "#B7D3D4" : "#5C9495";
  const ductsMaterial = await createMaterial(renderer, allocator, ductsColor, 1);
  const ductSuffix = ductsDummy ? " (placeholder)" : "";

  if (ductTopEnclosure) {
    await CustomBox3D({
      renderer,
      allocator,
      storeGeometry,
      parent: montageInternalNode,
      material: ductsMaterial,
      texture,
      x: montage.position.x,
      y: height - montage.position.y - ductTopEnclosure.size.height,
      z: montageDepth,
      width: ductTopEnclosure.size.width,
      height: ductTopEnclosure.size.height,
      depth: ductTopEnclosure.size.depth,
      nodeName: `Top Duct${ductSuffix}`,
    });
  }

  if (ductLeft && ductTopEnclosure) {
    await CustomBox3D({
      renderer,
      allocator,
      storeGeometry,
      parent: montageInternalNode,
      material: ductsMaterial,
      texture,
      x: montage.position.x,
      y:
        montage.position.y +
        ductTopEnclosure.size.height -
        ductTopEnclosure.size.height,
      z: montageDepth,
      width: ductLeft.size.width,
      height: ductLeft.size.height,
      depth: ductLeft.size.depth,
      nodeName: `Left Duct${ductSuffix}`,
    });
  }

  if (ductRight && ductTopEnclosure) {
    await CustomBox3D({
      renderer,
      allocator,
      storeGeometry,
      parent: montageInternalNode,
      material: ductsMaterial,
      texture,
      x: montage.position.x + montage.size.width - ductRight.size.width,
      y:
        montage.position.y +
        ductTopEnclosure.size.height -
        ductTopEnclosure.size.height,
      z: montageDepth,
      width: ductRight.size.width,
      height: ductRight.size.height,
      depth: ductRight.size.depth,
      nodeName: `Right Duct${ductSuffix}`,
    });
  }

  return {
    root,
    cabinetInternalNode,
    montageInternalNode,
    cabinetNode,
    montageNode,
  };
}